<script setup lang="ts">
  import { computed, ref } from 'vue';
  import type { FormElement, DropdownProperties } from '../../../model/form.types';
  import { useLabelPosition } from '../../../model/useLabelPosition';
  import { useElementContainerStyles } from '../../../model/useElementContainerStyles';
  import { useEditableField } from '../../../model/useFormBuilderReadonly';
  import {
    DROPDOWN_SELECT_CHEVRON_CLASSES,
    DROPDOWN_SELECT_CLASSES,
    DROPDOWN_SELECT_EMPTY_TEXT_CLASSES,
    DROPDOWN_SELECT_FILLED_TEXT_CLASSES,
  } from '../../../model/dropdown-select-classes';

  interface Props {
    element: FormElement & { properties: DropdownProperties };
  }

  const props = defineProps<Props>();

  const selectedValue = ref<string>('');

  const { isLabelLeft, labelClasses, labelStyles } = useLabelPosition(computed(() => props.element.properties));

  const { containerStyles } = useElementContainerStyles(
    computed(() => props.element.properties),
    'DROPDOWN'
  );

  const isEditable = useEditableField(() => props.element.properties.editable);
  const isBuilderLocked = computed(() => !isEditable.value);

  const alternatives = computed(() => props.element.properties.alternatives ?? []);
</script>

<template>
  <div
    class="w-full min-w-0 max-w-full box-border [font-family:var(--font-base)]"
    :class="{ 'flex items-center': isLabelLeft }"
    :style="containerStyles"
  >
    <label
      :for="`dropdown-${element.id}`"
      :class="[labelClasses, { 'shrink-0 whitespace-nowrap': isLabelLeft }]"
      :style="labelStyles"
    >
      {{ element.properties.label }}
    </label>
    <div
      class="w-full"
      :class="{ 'flex-1 min-w-0': isLabelLeft }"
    >
      <select
        :id="`dropdown-${element.id}`"
        v-model="selectedValue"
        :name="element.properties.name"
        :required="element.properties.required"
        :disabled="isBuilderLocked"
        :aria-label="element.properties.label || element.properties.name"
        :style="{ backgroundColor: 'white' }"
        :class="[
          DROPDOWN_SELECT_CLASSES,
          {
            [DROPDOWN_SELECT_FILLED_TEXT_CLASSES]: selectedValue,
            [DROPDOWN_SELECT_EMPTY_TEXT_CLASSES]: !selectedValue,
            [DROPDOWN_SELECT_CHEVRON_CLASSES]: !selectedValue,
            'dropdown-select--builder-locked pointer-events-none bg-white-01 opacity-100 ![cursor:inherit]':
              isBuilderLocked,
          },
        ]"
      >
        <option
          value=""
          disabled
          selected
        >
          {{ element.properties.placeholder }}
        </option>
        <option
          v-for="alternative in alternatives"
          :key="alternative.id"
          :value="alternative.value"
          class="p-1"
        >
          {{ alternative.value }}
        </option>
      </select>
    </div>
  </div>
</template>
